import React, { useMemo } from "react";
import { useDropzone } from 'react-dropzone'
import { FileUploadItem, IFileUploadItem, IRetryUploadSettings, IStartUploadSettings, IoipIcons, getExtension } from "ioip-base/lib/common";
import { DetailsList, IColumn, SelectionMode } from "@fluentui/react";
import { Column, DataTableIcon, Grid, Guid, Row } from "@ioip/core/lib";

const baseStyle: React.CSSProperties = {
    backgroundColor: 'rgb(249 249 249)',
    borderColor: '#a19f9d',
    borderRadius: '5px',
    borderStyle: 'dashed',
    borderWidth: 1,
    color: 'rgb(189, 189, 189)',
    display: 'flex',
    flex: 1,
    flexDirection: 'column',
    minHeight: '48px',
    outline: 'none',
    padding: '10px',
    transition: 'border .24s ease-in-out',
};
const activeStyle: React.CSSProperties = { borderColor: '#0078d4' };
const acceptStyle: React.CSSProperties = { borderColor: '#0078d4' };
const rejectStyle: React.CSSProperties = { borderColor: '#0078d4' };

export interface IFileSelectorProps {
    allowCreate: boolean;
    allowedExtensions?: string;
    columns?: IColumn[];
    errorMessage?: string;
    files: IFileUploadItem[];
    multiple: boolean;
    onChanged: (files: IFileUploadItem[]) => void;
    onClick?: (file: IFileUploadItem) => void;
    onCreate?: (name: string, size: number, uploadId: string) => Promise<IStartUploadSettings>;
    onRetry?: (file: IFileUploadItem, newName: string, newSize: number) => IRetryUploadSettings;
    onShowMessage?: (message: string, title: string) => void;
    onError?: (message: string) => void;
}

export const FileSelectorSede = (props: IFileSelectorProps): JSX.Element => {

    const { getRootProps, getInputProps, isDragActive, isDragAccept, isDragReject } = useDropzone({
        accept: { 'file/*': [ props.allowedExtensions ] },
        onDrop: (newFiles: File[]) => {

            if (!props.allowCreate) {
                return;
            }

            const tmp = props.files.slice();
            for (let i = 0; i < newFiles.length; i++) {
                const file: File = newFiles[ i ];
                if (tmp.find(o => o.name === file.name && o.size === file.size) == null) {
                    const extension = getExtension(file.name);
                    if (props.allowedExtensions.indexOf(file.type.toLowerCase()) < 0 && props.allowedExtensions.indexOf(extension.toLowerCase()) < 0) {
                        if (props.onError != null) {
                            props.onError(`Archivo no permitido: ${file.name}`);
                        }
                        break;
                    }
                    tmp.push({
                        nativeFile: file,
                        name: file.name,
                        size: file.size,
                        data: null,
                        error: false,
                        uploadId: Guid.newGuid().toString(),
                        isNew: true
                    });
                }
            }
            props.onChanged(tmp);

        }
    });

    const style: React.CSSProperties = useMemo(() => ({
        ...baseStyle,
        ...(isDragActive ? activeStyle : {}),
        ...(isDragAccept ? acceptStyle : {}),
        ...(isDragReject ? rejectStyle : {})
    }), [ isDragActive, isDragReject, isDragAccept ]);

    const allowDrop = (props.multiple || (!props.multiple && props.files.length === 0));

    //const files = new List<IFileUploadItem>(props.files || []).OrderBy(o => o.name).ToArray();
    const files = props.files || [];

    const renderFiles = () => {
        return <Grid>
            <DetailsList
                selectionMode={SelectionMode.none}
                isHeaderVisible={false}
                columns={[
                    {
                        key: 'icon',
                        name: '',
                        fieldName: 'UserName',
                        minWidth: 24,
                        maxWidth: 24,
                        isResizable: false,
                        onRender: (file: IFileUploadItem) => {
                            let icon: string;
                            switch (getExtension(file.name || '').trim().toLowerCase()) {

                                case 'docx':
                                case 'doc':
                                    icon = IoipIcons.Docx;
                                    break;

                                case 'xlsx':
                                case 'xls':
                                    icon = IoipIcons.Xlsx;
                                    break;

                                case 'pptx':
                                case 'ppt':
                                    icon = IoipIcons.Pptx;
                                    break;

                                case 'pdf':
                                    icon = IoipIcons.Pdf;
                                    break;

                                case 'msg':
                                    icon = IoipIcons.Mail;
                                    break;

                                case 'txt':
                                    icon = IoipIcons.Txt;
                                    break;

                                case 'jpg':
                                case 'jpeg':
                                case 'png':
                                case 'bmp':
                                case 'icon':
                                    icon = IoipIcons.Picture;
                                    break;

                                default:
                                    icon = IoipIcons.File;
                                    break;

                            }
                            return <DataTableIcon icon={icon} />;
                        }
                    },
                    {
                        key: 'name',
                        name: 'Nombre',
                        fieldName: 'name',
                        minWidth: 200,
                        isResizable: true,
                        onRender: (file: IFileUploadItem) => <FileUploadItem
                            file={file}
                            onCreate={props.onCreate}
                            onClick={() => {
                                if (typeof props.onClick === 'function') {
                                    props.onClick(file);
                                }
                            }}
                            onRetry={props.onRetry}
                            onComplete={(nFile) => {
                                const items = props.files.map(item => {
                                    if (item == nFile) {
                                        item.data = nFile.data;
                                    }
                                    return item;
                                })
                                props.onChanged(items);
                            }}
                            onError={props.onError}
                            onMessage={props.onShowMessage}
                        />
                    },
                    ...(props.columns || [])
                ]}
                items={files}
            />
        </Grid>;
    }

    return <>
        <Row>
            <Column size={12}>
                {
                    <div>
                        <div {...getRootProps({ style })} className="archivox-file-selector-2">
                            <input {...getInputProps()} />
                            {
                                isDragAccept && allowDrop && <>
                                    {renderFiles()}
                                    <div
                                        style={{
                                            "color": "#4d6ecf",
                                            "position": "absolute",
                                            "top": "0px",
                                            "left": "0px",
                                            "width": "100%",
                                            "textAlign": "center",
                                            "height": "100%",
                                            "background": "white",
                                            "border": "dashed 2px #4d6ecf"
                                        }}
                                    >
                                        <div style={{
                                            "position": "absolute",
                                            "lineHeight": "100%",
                                            "height": "100%",
                                            "backgroundImage": "url(https://img.icons8.com/fluency/2x/video-file.png)",
                                            "backgroundRepeat": "no-repeat",
                                            "backgroundPosition": "center",
                                            "width": "100%"
                                        }}></div>
                                    </div>
                                </>
                            }
                            {
                                isDragReject && <>
                                    {renderFiles()}
                                    <div>Suelte los archivos aquí</div>
                                </>
                            }
                            {
                                !isDragActive && <>
                                    {
                                        props.files.length === 0 && <div style={{ color: 'black', textAlign: 'center', paddingTop: '10px' }}>
                                            <p>Si usted cuenta con archivos que soporten su solicitud adjuntelos aquí.</p>
                                            <hr style={{ borderTop: '1px solid #333', width: '50%', margin: '10px auto' }} />
                                            <p>Nota: Recuerde que los formatos permitidos para adjuntar son: {'*.' + props.allowedExtensions.toUpperCase().split(',').join(' *.')}</p>
                                        </div>
                                    }
                                    {
                                        props.files.length > 0 && <div
                                            onClick={e => {
                                                e.preventDefault();
                                                e.stopPropagation();
                                            }}
                                            className="dt-container">
                                            {
                                                renderFiles()
                                            }
                                        </div>
                                    }
                                </>
                            }
                        </div>
                    </div>
                }
            </Column>
        </Row>
        <Row>
            <Column size={12}>
                <small style={{ color: '#bb3737' }}>{props.errorMessage}</small>
            </Column>
        </Row>
    </>

}